import {
  HttpClient,
  HttpEvent,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { inject, Inject, Injectable } from "@angular/core";
import { EnvConfig, Environment, CacheService } from "@doc/core";
import { ApiUrls } from "../document-urls";
import {
  BaseResponse,
  EventDeleteRequest,
  EventGetListRequest,
  EventGetListResponse,
  EventInsertRequest,
} from "../models";
import { PaginatedTable } from "@doc/utils";
import { BehaviorSubject, Observable, map, of, tap, timer } from "rxjs";
import { options } from "@fullcalendar/core/preact";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from "@angular/router";
import { interval, Subscription } from "rxjs";
import { catchError, switchMap, takeUntil } from "rxjs/operators";
import { NgxSpinnerService } from "ngx-spinner";
@Injectable({
  providedIn: "root",
})
export class DocumentService {
  private http = inject(HttpClient);
  private apiUrl: string = "";
  private baseUrl: string;
  private previousUrl: string | null = null;
  private previousQueryParams: any = null;
  private currentUrl: string | null = null;
  private statusSubscription: Subscription | null = null;
  private processTimer: number = 0;
  private processDocInterval: number = 0;

  private driveType: string = "";
  constructor(
    @Inject(EnvConfig) private config: Environment,
    private cacheService: CacheService,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    public router: Router
  ) {
    this.baseUrl = this.config.mockUrl;
    this.apiUrl = this.config.apiUrl;
    this.processTimer = this.config.ProcessDocApiLimit;
    this.processDocInterval = this.config.ProcessDocApiInterval;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.queryParams) {
          this.previousQueryParams = navigation.extras.queryParams;
        }
      }

      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
      }
    });
  }

  getPreviousUrl(): { url: string | null; queryParams: any } {
    return { url: this.previousUrl, queryParams: this.previousQueryParams };
  }

  getFoldersandPermissions(body: any) {
    const link = this.apiUrl + ApiUrls.getFolders;
    return this.http.post<any>(link, body);
  }

  getFolders(body: any) {
    const link = this.apiUrl + ApiUrls.getFiles;
    return this.http.post<any>(link, body);
  }

  getFolderData(folderType: string, body: any) {
    const links: any = {
      "my-drive": ApiUrls.getMyFolders,
      "shared-with-me": ApiUrls.getFoldersSharedWithUser,
      "shared-by-me": ApiUrls.getFolderSharedByUser,
    };

    const link = this.apiUrl + (links[folderType] || links["my-drive"]);
    return this.http.post<any>(link, body);
  }

  getFolderNames(body: any) {
    const link = this.apiUrl + ApiUrls.getMyFoldersNames;
    return this.http.post<any>(link, body);
  }

  getFolderByCategory(body: any) {
    const link = this.apiUrl + ApiUrls.getMyFolders;
    return this.http.post<any>(link, body);
  }

  getAllFavourites(body: any) {
    const link = this.apiUrl + ApiUrls.getAllFavourites;
    return this.http.post<any>(link, body);
  }

  saveDocToFavourite(body: any) {
    const link = this.apiUrl + ApiUrls.saveDocToFavourite;
    return this.http.post<any>(link, body);
  }

  saveDocToFolder(body: any) {
    const link = this.apiUrl + ApiUrls.saveDocToFolder;
    return this.http.post<any>(link, body);
  }

  transferFileToFolder(body: any) {
    const link = this.apiUrl + ApiUrls.transferFileToFolder;
    return this.http.post<any>(link, body);
  }

  deleteFromFavourites(body: any) {
    const link = this.apiUrl + ApiUrls.deleteFromFavourites;
    return this.http.post<any>(link, body);
  }

  // createFolder(body: any) {
  //   const link = `${this.apiUrl}/api/Folder/create`;
  //   return this.http.post<any>(link, body);
  // }
  createFolder(body: any) {
    const link = this.apiUrl + ApiUrls.createFolder;
    return this.http.post<any>(link, body);
  }

  // updateFolder(id: number, body: any) {
  //   const link = `${this.apiUrl}/api/Folder/update/${id}`;
  //   return this.http.put<any>(link, body);
  // }
  updateFolder(body: any) {
    const link = this.apiUrl + ApiUrls.updateFolder;
    return this.http.post<any>(link, body);
  }

  updateFolderByCategory(id: number, body: any) {
    const link = `${this.apiUrl}/api/Folder/update/${id}`;
    return this.http.post<any>(link, body);
  }
  // deleteFolder(id: number) {
  //   const link = `${this.apiUrl}/api/Folder/delete/${id}`;
  //   return this.http.delete<any>(link);
  // }
  deleteFolder(body: any) {
    const link = this.apiUrl + ApiUrls.deleteFolder;
    return this.http.post<any>(link, body);
  }

  getAllFilesInFolder(body: any) {
    const link = this.apiUrl + ApiUrls.getfilesinfolder;
    return this.http.post<any>(link, body);
  }
  getOutbox(body: any) {
    const link = this.apiUrl + ApiUrls.getOutbox;
    return this.http.post<any>(link, body);
  }
  transferDocumentOwnership(body: any) {
    const link = this.apiUrl + ApiUrls.transferDocownership;
    return this.http.post<any>(link, body);
  }
  getAdminOutbox(body: any) {
    const link = this.apiUrl + ApiUrls.getAdminOutbox;
    return this.http.post<any>(link, body);
  }
  getInbox(body: any) {
    const link = this.apiUrl + ApiUrls.getInbox;
    return this.http.post<any>(link, body);
  }
  getTasks(body: any) {
    const link = this.apiUrl + "/api/WorkFlowDetails/GetTasks";
    return this.http.post<any>(link, body);
  }

  getMeetingList(body: any) {
    const link = this.apiUrl + ApiUrls.CalenderMeetings;
    return this.http.post<any>(link, body);
  }

  getTasksByUser(body: any) {
    const link = this.apiUrl + ApiUrls.GetTasksByUser;
    return this.http.post<any>(link, body);
  }
  updateTasksByUser(body: any) {
    const link = this.apiUrl + ApiUrls.UpdateTaskByUser;
    return this.http.post<any>(link, body);
  }
  getAdminInbox(body: any) {
    const link = this.apiUrl + ApiUrls.getAdminInbox;
    return this.http.post<any>(link, body);
  }
  askQuestion(body: any) {
    const link = this.apiUrl + "/api/DocumentProcessing/Askquestion";
    return this.http.post<any>(link, body);
  }
  getEmployeeInbox(body: any) {
    const link = this.apiUrl + ApiUrls.getEmployeeInbox;
    return this.http.post<any>(link, body);
  }

  deleteFileFromFolder(body: any) {
    const link = this.apiUrl + ApiUrls.deleteFileFromFolder;
    return this.http.post<any>(link, body);
  }

  deleteFolderByCategory(id: number) {
    const link = `${this.apiUrl}/api/Folder/delete/${id}`;
    return this.http.post<any>(link, id);
  }

  shareFolder(body: any) {
    const link = `${this.apiUrl}${ApiUrls.createFolderPermission}`;
    return this.http.post<any>(link, body);
  }

  getFileData(fileType: string, body: any) {
    const links: any = {
      "my-drive": ApiUrls.getMyFiles,
      "shared-with-me": ApiUrls.getFilesSharedWithUser,
      "shared-by-me": ApiUrls.getFilesSharedByUser,
    };

    const link = this.apiUrl + (links[fileType] || links["my-drive"]);
    return this.http.post<any>(link, body);
  }

  getFileDataByCategory(body: any) {
    const link = this.apiUrl + ApiUrls.getMyFiles;
    return this.http.post<any>(link, body);
  }

  getFileById(id: number) {
    const link = this.apiUrl + ApiUrls.getFileById + `/${id}`;
    return this.http.get<any>(link);
  }
  getFileByPath(filePath: string) {
    const link =
      this.apiUrl + ApiUrls.getFileByPath + `/${encodeURIComponent(filePath)}`;
    return this.http.get<any>(link);
  }

  updateFile(id: number, body: any) {
    const link = `${this.apiUrl}/api/File/update/${id}`;
    return this.http.put<any>(link, body);
  }

  updateFileByCategory(id: number, body: any) {
    const link = `${this.apiUrl}/api/File/update/${id}`;
    return this.http.post<any>(link, body);
  }

  deleteFile(id: number) {
    const link = `${this.apiUrl}/api/File/delete/${id}`;
    return this.http.delete<any>(link);
  }

  deleteFileByCategory(id: number) {
    const link = `${this.apiUrl}/api/File/delete/${id}`;
    return this.http.post<any>(link, id);
  }

  shareFile(body: any) {
    const link = `${this.apiUrl}${ApiUrls.createFilePermission}`;
    return this.http.post<any>(link, body);
  }

  createFolderPermission(body: any) {
    const link = `${this.apiUrl}/api/Folder/createFolderPermission`;
    return this.http.post<any>(link, body);
  }

  removeFolderPermission(id: any) {
    const link = `${this.apiUrl}/api/Folder/deleteFolderPermission?permissionId=${id}`;
    return this.http.post<any>(link, { permissionId: id });
  }

  removeFilePermission(id: any) {
    const link = `${this.apiUrl}/api/File/deleteFilePermission?permissionId=${id}`;
    return this.http.post<any>(link, { permissionId: id });
  }

  getDocumentProcessingList(payload: any) {
    const link = `${this.apiUrl}/api/DocumentProcessing/getDocumentProcessingList`;
    return this.http.post<any>(link, payload);
  }

  uploadFiles(formData: FormData) {
    const headers = new HttpHeaders({
      "content-type": "multipart/form-data",
    });
    const link = this.apiUrl + ApiUrls.upload;
    return this.http.post(link, formData, { headers });
  }

  uploadRejectedFiles(formData: FormData) {
    const headers = new HttpHeaders({
      "content-type": "multipart/form-data",
    });
    const link = this.apiUrl + ApiUrls.reupload;
    return this.http.post(link, formData, { headers });
  }

  downloadFile(filePath: string): Observable<HttpEvent<Blob>> {
    const url = this.apiUrl + ApiUrls.download + `/${filePath}`;
    return this.http.get<Blob>(url, {
      observe: "events",
      responseType: "blob" as "json",
    });
  }

  // downloadFile(filePath: any): Observable<{blob: Blob, type: string}> {
  //   const url = this.apiUrl + ApiUrls.download + `/${filePath}`;
  //   return this.http.get(url, {
  //     observe: 'response',
  //     responseType: 'blob'
  //   }).pipe(
  //     map((response:any) => {
  //       return {
  //         blob: response.body,
  //         type: response.headers.get('Content-Type')
  //       };
  //     })
  //   );
  // }

  generateRequest(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/GenerateRequest`;
    return this.http.post<any>(url, payload);
  }
  getAllSearchData(): Observable<any> {
    const url = `${this.apiUrl}/api/History/GetAllSearchData`;
    return this.http.get<any>(url);
  }
  getAllSearchDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/GetSearchDetails`;
    return this.http.post<any>(url, payload);
  }
  getImage(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/getImage`;
    return this.http.post<any>(url, payload);
  }

  updateGeneratedDocuments(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/uploadGenDoc`;
    return this.http.post<any>(url, payload);
  }

  SearchCustomsData(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/SearchDocument`;
    return this.http.post<any>(url, payload);
  }

  TriggerMail(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/TriggerMail`;
    return this.http.post<any>(url, payload);
  }
  GetCustomsDocument(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetDocument`;
    return this.http.post<any>(url, payload);
  }

  GetAllDocuments(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getAllDocByType;
    return this.http.post<any>(url, payload);
  }

  GetAllBrowseDocuments(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getAllDocuments;
    return this.http.post<any>(url, payload);
  }

  getAllSharedDocuments(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getSharedDocument;
    return this.http.post<any>(url, payload);
  }

  getSharedDocumentIds(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getSharedDocumentIds;
    return this.http.post<any>(url, payload);
  }

  getSharedUsers(docId: any) {
    const url = this.apiUrl + ApiUrls.getSharedUsers + "/" + docId;
    return this.http.get<any>(url);
  }

  shareDocument(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.shareDocument;
    return this.http.post<any>(url, payload);
  }

  getDocumentSharedTemplates(): Observable<any> {
    const url = this.apiUrl + ApiUrls.getDocumentSharedTemplates;
    return this.http.get<any>(url);
  }

  revokeShareDocument(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.revokeShareDocument;
    return this.http.post<any>(url, payload);
  }

  GetDocumentDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetDocumentData`;
    return this.http.post<any>(url, payload);
  }

  ReadDocumentDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/readDataDoc`;
    return this.http.post<any>(url, payload);
  }

  OnlyReadDocumentDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/onlyReadDataDoc`;
    return this.http.post<any>(url, payload);
  }
  saveDocD(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/saveReadDataDoc`;
    return this.http.post<any>(url, payload);
  }

  ReadAllGeneratedDocuments(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/ReadAllGeneratedDocuments`;
    return this.http.post<any>(url, payload);
  }

  Deletedocument(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/DeleteDocument`;
    return this.http.post<any>(url, payload);
  }
  ProcessWorkflow(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/ProcessWorkFlow`;
    return this.http.post<any>(url, payload);
  }
  CreateTranslationWorkflow(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/InitiateTranslation`;
    return this.http.post<any>(url, payload);
  }
  getPendingFilesByUser(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/File/GetAllPendingFilesByUser`;
    return this.http.post<any>(url, payload);
  }

  fileReview(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/File/ApproveOrReject`;
    return this.http.post<any>(url, payload);
  }

  fileApprovalHistoryById(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/File/GetAllFilesHistoryByFileId`;
    return this.http.post<any>(url, payload);
  }
  getDocumentDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetDocumentDetails/` + payload;
    return this.http.get<any>(url);
  }

  GetUploadedFile(payload: any): Observable<any> {
    const cacheKey = `uploadedFile_${payload}`;
    const cachedData = this.cacheService.getCache(cacheKey);
    if (cachedData) {
      console.log("Serving from cache");
      return of(cachedData);
    }

    const url = `${this.apiUrl}/api/Document/GetUploadedFile/` + payload;
    return this.http
      .get<any>(url, {
        responseType: "blob" as "json",
      })
      .pipe(tap((data) => this.cacheService.setCache(cacheKey, data)));
  }

  GetTranslatedFile(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetTransaltedFile/` + payload;
    return this.http.get<any>(url, {
      responseType: "blob" as "json",
    });
  }

  GetUploadedProcessedFile(payload: any): Observable<any> {
    const url =
      `${this.apiUrl}/api/DocumentProcessing/getProcessedDocumentFile/` +
      payload;
    return this.http.get<any>(url, {
      responseType: "blob" as "json",
    });

    responseType: "blob" as "json";
  }

  private updateOption = new BehaviorSubject<any>(null);
  checkUploadOption = this.updateOption.asObservable();
  passUpdateOption(option: any) {
    this.updateOption.next(option);
  }

  // getFileAccessDetails(payload: any): Observable<any> {
  //   const url = `${this.apiUrl}/api/File/GetAllFilesPermmissionByFileId`;
  //   return this.http.post<any>(url, payload)
  // }

  getFileAccessDetailsById(id: number): Observable<any> {
    const url = `${this.apiUrl}/api/File/getFilePermissionById?id=${id}`;
    return this.http.get<any>(url);
  }

  getFolderAccessDetailsById(id: number): Observable<any> {
    const url = `${this.apiUrl}/api/Folder/getFolderPermissionsById?id=${id}`;
    return this.http.get<any>(url);
  }

  GetAllFilesPermmissionByFileId(payload: any): Observable<any> {
    //GetAllFilesPermmissionByFileId'
    const url = `${this.apiUrl}/api/File/GetAllFilesPermmissionByFileId`;
    return this.http.post<any>(url, payload);
  }

  GetAllFilesPermmissionByFolderId(payload: any): Observable<any> {
    //GetAllFilesPermmissionByFileId'
    const url = `${this.apiUrl}/api/Folder/GetAllFolderPermmissionByFolderId`;
    return this.http.post<any>(url, payload);
  }

  getUserPendingTasks(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetAllPendingTasksByUser`;
    return this.http.post<any>(url, payload);
  }

  //NEW Services
  createReportAgrMou(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.createReportAgrMou;
    return this.http.post<BaseResponse>(url, payload);
  }
  createReportPom(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.createReportPom;
    return this.http.post<BaseResponse>(url, payload);
  }

  searchAgrMou(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.searchAgrMou;
    return this.http.post<BaseResponse>(url, payload);
  }

  searchPom(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.searchPom;
    return this.http.post<BaseResponse>(url, payload);
  }

  getFilter(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getFilter + payload.docType;
    return this.http.get<BaseResponse>(url);
  }

  getAllFilter(): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetFiltersByUser`;
    return this.http.get<any>(url);
  }

  createFilter(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.createFilter;
    return this.http.post<BaseResponse>(url, payload);
  }

  deleteFilter(id: number) {
    const link = this.apiUrl + ApiUrls.deleteFilter + `/${id}`;
    return this.http.post<any>(link, id);
  }

  // downloadReport(payload: any): Observable<any> {
  //   const url = this.apiUrl + ApiUrls.downloadReport;
  //   return this.http.post(url, payload);
  // }

  downloadReport(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.downloadReport;
    return this.http.post(url, requestObject, { responseType: "blob" });
  }

  exportInbox(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.exportInbox;
    return this.http.post(url, requestObject, { responseType: "blob" });
  }

  UploadAiProcessFile(formData: FormData): Observable<any> {
    const headers = new HttpHeaders({
      "content-type": "multipart/form-data",
      "X-Skip-Loader": "true",
    });
    const link = this.apiUrl + ApiUrls.uploadai;
    return this.http.post(link, formData, { headers });
  }

  ReadAiInfo(payload: any): Observable<any> {
    const url =
      `${this.apiUrl}/api/DocumentProcessing/getProcessedDocument/` + payload;
    return this.http.get<any>(url);
  }

  SaveDocument(payload: any): Observable<any> {
    // const url = `${this.apiUrl}/api/Document/SaveDocument`;
    const headers = new HttpHeaders({
      "content-type": "multipart/form-data",
    });
    // 'https://obliging-formerly-titmouse.ngrok-free.app' +
    const url = this.apiUrl + "/api/Document/SaveDocument";
    return this.http.post<any>(url, payload, { headers });
  }

  saveTodraft(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      "content-type": "multipart/form-data",
    });
    // 'https://obliging-formerly-titmouse.ngrok-free.app' +
    const url = this.apiUrl + ApiUrls.saveToDraft;
    return this.http.post<any>(url, payload, { headers });
  }
  loadDraftValues(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getDraftDataById + payload;
    return this.http.get<any>(url);
  }

  retryAiProcessing(documentProcessingId: any, payload: any) {
    const url =
      this.apiUrl + ApiUrls.retryAIProcessing + "/" + documentProcessingId;
    return this.http.post<any>(url, payload);
  }
  documentSoftDelete(documentProcessingId: any) {
    const url =
      this.apiUrl + ApiUrls.documentSoftDelete + "/" + documentProcessingId;
    return this.http.post<any>(url, null);
  }

  changeParticipationType(body: any) {
    const url = this.apiUrl + ApiUrls.changeParticipationType;
    return this.http.post<any>(url, body);
  }

  createAndSaveDocument(body: any) {
    const url = this.apiUrl + ApiUrls.createDocument;
    return this.http.post<any>(url, body);
  }

  getSubjectByDocType(docType: any) {
    const url = this.apiUrl + ApiUrls.getSubjectByDocType + "/" + docType;
    return this.http.get<any>(url);
  }

  getAllDraftData(body: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getDraftDocuments;
    return this.http.post<any>(url, body);
  }

  getUploadedAttachments(id: any) {
    const url = this.apiUrl + ApiUrls.getUploadedAttachments + "/" + id;
    return this.http.get(url, {
      responseType: "blob",
      observe: "response",
    });
  }

  downloadPomReport(id: any) {
    const url = this.apiUrl + ApiUrls.getPOMReportTemplate + "/" + id;
    return this.http.get(url, { responseType: "blob" });
  }

  getUploadedPomReportAttachments(id: any) {
    const url =
      this.apiUrl + ApiUrls.getUploadedPomReportAttachments + "/" + id;
    return this.http.get(url, {
      responseType: "blob",
      observe: "response",
    });
  }

  viewUpoadedPomReportAttachments(id: any) {
    const url =
      this.apiUrl + ApiUrls.getUploadedPomReportAttachments + "/" + id;
    return this.http.get(url, { responseType: "blob" });
  }
  viewUpoadedPomAttachments(id: any) {
    const url = this.apiUrl + ApiUrls.getUploadedAttachments + "/" + id;
    return this.http.get(url, { responseType: "blob" });
  }
  deleteDraftDocument(body: any) {
    const url = this.apiUrl + ApiUrls.deleteDraftDocuments;
    return this.http.post<any>(url, body);
  }

  getParticipantNames(docId: any) {
    const url = this.apiUrl + ApiUrls.GetMeetingParticipants + "/" + docId;
    return this.http.get<any>(url);
  }

  getObservationByDocId(docId: any) {
    const url = this.apiUrl + ApiUrls.getDocumentObservation + "/" + docId;
    return this.http.get<any>(url);
  }

  getLatestDevelopmentByDocId(docId: any) {
    const url = this.apiUrl + ApiUrls.getLatestDevelopment + "/" + docId;
    return this.http.get<any>(url);
  }

  addEditObservation(payload: any) {
    const url = this.apiUrl + ApiUrls.addUpdateObservation;
    return this.http.post<any>(url, payload);
  }

  addUpdateLatestDevelopment(payload: any) {
    const url = this.apiUrl + ApiUrls.addUpdateLatestDevelopment;
    return this.http.post<any>(url, payload);
  }

  deleteObservation(docId: any) {
    const url = this.apiUrl + ApiUrls.deleteObservation + "/" + docId;
    return this.http.post<any>(url, null);
  }

  deleteLatestDevelopment(docId: any) {
    const url = this.apiUrl + ApiUrls.deleteLatestDevelopment + "/" + docId;
    return this.http.post<any>(url, null);
  }

  createTask(body: any) {
    const url = this.apiUrl + ApiUrls.createTasks;
    return this.http.post<any>(url, body);
  }

  getDocumentLog(documentId: any, type: any) {
    const url =
      this.apiUrl + ApiUrls.documentLogs + "/" + documentId + "/" + type;
    return this.http.get<any>(url);
  }
  getTaskLog(documentId: any, type: any) {
    const url = this.apiUrl + ApiUrls.taskLogs + "/" + documentId + "/" + type;
    return this.http.get<any>(url);
  }
  getWorkflowLog(documentId: any) {
    const url = this.apiUrl + ApiUrls.workflowLog + "/" + documentId;
    return this.http.get<any>(url);
  }

  uploadUpdateAttachment(body: any, params: any) {
    const headers = new HttpHeaders({
      "content-type": "multipart/form-data",
    });
    const url = this.apiUrl + ApiUrls.uploadUpdateAttachment;
    return this.http.post<any>(url, body, { headers: headers, params: params });
  }

  uploadUpdatePomReport(body: any, params: any) {
    const headers = new HttpHeaders({
      "content-type": "multipart/form-data",
    });
    const url = this.apiUrl + ApiUrls.uploadUpdatePomReport;
    return this.http.post<any>(url, body, { headers: headers, params: params });
  }

  customPomReportUpload(payload: any) {
    const url = this.apiUrl + ApiUrls.customPomReportUpload;
    return this.http.post<any>(url, payload);
  }

  getAllDocAttachment(docId: any) {
    const url = this.apiUrl + ApiUrls.getAllDocAttachment + "/" + docId;
    return this.http.get<any>(url);
  }

  getAllDocPomReport(docId: any) {
    const url = this.apiUrl + ApiUrls.getAllDocPomReport + "/" + docId;
    return this.http.get<any>(url);
  }

  deleteDocAttachment(docId: any) {
    const url = this.apiUrl + ApiUrls.deleteDocAttachment + "/" + docId;
    return this.http.post<any>(url, null);
  }

  deleteDocPomReport(docId: any) {
    const url = this.apiUrl + ApiUrls.deleteDocPomReport + "/" + docId;
    return this.http.post<any>(url, null);
  }

  GetSubjectsByDocumentType(docId: any) {
    const url = this.apiUrl + ApiUrls.getSelectedDocId + "/" + docId;
    return this.http.get<any>(url);
  }

  GetSubjectsDraftByDocumentType(docId: any, payload: any) {
    const url = this.apiUrl + ApiUrls.getSubjectDraftByDocType + "/" + docId;
    return this.http.post<any>(url, payload);
  }

  ParticipationNumberListOnSubject(body: any) {
    const url = this.apiUrl + ApiUrls.participationNumberListOnSubject;
    return this.http.post<any>(url, body);
  }

  GetAllDocSubjects(body: boolean) {
    const url = this.apiUrl + ApiUrls.getAllSelected;
    return this.http.post<any>(url, body);
  }

  GetDocSubjectsOfUser() {
    const url = this.apiUrl + ApiUrls.getSelectedSubject;
    return this.http.get<any>(url);
  }

  convertToDateToIso(dateString: any | string): Date | string {
    if (dateString != null && dateString != undefined && dateString !== "") {
      const year = dateString.year;
      const month = dateString.month - 1;
      const day = dateString.day;
      const utcDate = new Date(Date.UTC(year, month, day));
      const isoString = utcDate.toISOString();
      return isoString;
    }
    return "";
  }

  formatYear(value: any): number | string {
    if (!value) return "";
    // If the value is a Date object
    if (value instanceof Date) {
      return value.getFullYear();
    }

    // If the value is an ISO string
    if (typeof value === "string") {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.getFullYear();
      }
    }

    // If the value is already a valid 4-digit year
    if (typeof value === "number" && value.toString().length === 4) {
      return value;
    }

    // Fallback
    console.warn("Invalid date format:", value);
    return "";
  }

  getTaskListByDocId(docId: any) {
    const url = this.apiUrl + ApiUrls.getTaskListByDocId + "/" + docId;
    return this.http.get<any>(url);
  }

  deleteDocument(body: any) {
    const url = this.apiUrl + ApiUrls.deleteDocument;
    return this.http.post<any>(url, body);
  }

  getDeletedDocuments(body: any) {
    const url = this.apiUrl + ApiUrls.getDeletedDocuments;
    return this.http.post<any>(url, body);
  }
  restoreDocument(docId: any) {
    const url = this.apiUrl + ApiUrls.restoreDocument + "/" + docId;
    return this.http.get<any>(url);
  }

  updateDocument(body: any) {
    const url = this.apiUrl + ApiUrls.updateDocument;
    return this.http.post<any>(url, body);
  }

  getDocumentTranslationDetails(docId: any) {
    const url = this.apiUrl + ApiUrls.documentTranslation + "/" + docId;
    return this.http.get<any>(url);
  }
  saveDocumentTranslation(body: any) {
    const url = this.apiUrl + ApiUrls.saveTranslation;
    return this.http.post<any>(url, body);
  }

  translateTheDoc(docId: any) {
    const url = this.apiUrl + ApiUrls.translateDoc + "/" + docId;
    return this.http.get<any>(url);
  }

  GetDocumentWorkflowRecord(body: any) {
    const url = this.apiUrl + ApiUrls.GetDocumentWorkflowRecord;
    return this.http.post<any>(url, body);
  }

  GetTopNotifications() {
    const url = this.apiUrl + ApiUrls.GetTopNotifications;
    return this.http.post<any>(url, null);
  }

  getExportLogData(body: any) {
    const url = this.apiUrl + ApiUrls.exportLogData;
    return this.http.post(url, body, { responseType: "blob" as "json" });
  }

  getTaskExportLogData(body: any) {
    const url = this.apiUrl + ApiUrls.exportTaskLogData;
    return this.http.post(url, body, { responseType: "blob" as "json" });
  }

  getExportDevelopment(body: any): Observable<Blob> {
    const url = this.apiUrl + ApiUrls.exportdevelopment;
    return this.http.post<Blob>(url, body, { responseType: 'blob' as 'json' });
  }


  shareDevelopment(body: any) {
    const url = this.apiUrl + ApiUrls.sharedevelopment;
    return this.http.post<any>(url, body);
  }

  shareIdentificationCard(body: any) {
    const url = this.apiUrl + ApiUrls.shareIdentificationCard;
    return this.http.post<any>(url, body);
  }

  shareAdminInboxData(body: any) {
    const url = this.apiUrl + ApiUrls.shareAdminInbox;
    return this.http.post<any>(url, body);
  }

  sharePersonalInboxData(body: any) {
    const url = this.apiUrl + ApiUrls.sharePersonalInbox;
    return this.http.post<any>(url, body);
  }

  shareReport(body: any) {
    const url = this.apiUrl + ApiUrls.shareReport;
    return this.http.post<any>(url, body);
  }

  downloadDocument(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.downloadDocument;
    return this.http.post(url, requestObject, { responseType: "blob" });
  }

  addNewLiaisonOfficer(body: any) {
    const url = this.apiUrl + ApiUrls.saveLiaisonOfficers;
    return this.http.post<any>(url, body);
  }

  addNewParticipants(body: any) {
    const url = this.apiUrl + ApiUrls.saveParticipants;
    return this.http.post<any>(url, body);
  }

  getLiaisonOfficerById(docId: any) {
    const url = this.apiUrl + ApiUrls.getLiaisonOfficerById + "/" + docId;
    return this.http.get<any>(url);
  }

  getParticipantsById(docId: any) {
    const url = this.apiUrl + ApiUrls.getParticipantsById + "/" + docId;
    return this.http.get<any>(url);
  }

  deleteLiaisonOfficer(docId: number, userId: number) {
    const link = `${this.apiUrl}/api/Document/DeleteLiaisonOfficer/${docId}/${userId}`;
    return this.http.post<any>(link, {});
  }

  deleteParticipant(docId: number, userId: number) {
    const link = `${this.apiUrl}/api/Document/DeleteParticipationNames/${docId}/${userId}`;
    return this.http.post<any>(link, {});
  }

  downloadIdentificationCard(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.downloadIdentificationCard;
    return this.http.post(url, requestObject, { responseType: "blob" });
  }

  updateIdCard(body: any) {
    const url = this.apiUrl + ApiUrls.updateIdCard;
    return this.http.post<any>(url, body);
  }

  getIdCardByDocId(payload: any) {
    const url = this.apiUrl + ApiUrls.getIdCardByDocId;
    return this.http.post<any>(url, payload);
  }

  getProcessDocumentStatus(docId: any) {
    const url = this.apiUrl + ApiUrls.getDocumentProcessingStatus;
    const params = new HttpParams().set("Id", docId);
    const headers = new HttpHeaders({ "X-Skip-Loader": "true" });
    return this.http.get<any>(url, { params, headers });
  }
  ExportSingleReport(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.exportSingleReport;
    return this.http.post(url, requestObject, { responseType: "blob" });
  }
  getPOMReportTemplateById(docId: any) {
    const url = this.apiUrl + ApiUrls.getPOMReportTemplateById + "/" + docId;
    return this.http.get<any>(url);
  }

  // startBackgroundPolling(documentId: string, callback: (status: string) => void) {
  //   this.statusSubscription = interval(10000).pipe(
  //     switchMap(() =>
  //       this.getProcessDocumentStatus(documentId).pipe(
  //         catchError((err) => {
  //           console.error('Polling error:', err);
  //           return of(null);
  //         })
  //       )
  //     )
  //   ).subscribe((res) => {
  //     if (res?.status && res?.data?.isDocumentProcessed) {
  //       callback('completed');
  //       this.stopBackgroundPolling();
  //     }
  //   });
  // }

  startBackgroundPolling(
    documentId: string,
    callback: (status: "completed" | "timeout" | "processError") => void
  ) {
    const timeout$ = timer(this.processTimer);
    this.statusSubscription = interval(this.processDocInterval)
      .pipe(
        takeUntil(timeout$),
        switchMap(() =>
          this.getProcessDocumentStatus(documentId).pipe(
            catchError((err) => {
              console.error("Polling error:", err);
              return of(null);
            })
          )
        )
      )
      .subscribe({
        next: (res) => {
          if (
            res?.status &&
            res?.data?.isDocumentProcessed &&
            !res?.data?.isDocumentFailed
          ) {
            callback("completed");
            this.stopBackgroundPolling();
          } else if (
            res?.status &&
            res?.data?.isDocumentProcessed &&
            res?.data?.isDocumentFailed
          ) {
            callback("processError");
            this.stopBackgroundPolling();
          }
        },
        complete: () => {
          callback("timeout");
          this.stopBackgroundPolling();
        },
      });
  }

  stopBackgroundPolling() {
    this.statusSubscription?.unsubscribe();
    this.statusSubscription = null;
  }

  getPersonalInboxCount(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/GetPersonalInboxCount`;
    return this.http.post<any>(url, payload);
  }

  getPersonalOutboxCount(): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/GetPersonalOutboxCount`;
    return this.http.get<any>(url);
  }

  getAdminInboxCount(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/GetAdminInboxCount`;
    return this.http.post<any>(url, payload);
  }

  getAdminOutboxCount(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/GetAdminOutboxCount`;
    return this.http.post<any>(url, payload);
  }

  updateIsReadNotifications(body: any) {
    const link = this.apiUrl + ApiUrls.updateIsReadNotifications;
    return this.http.post<any>(link, body);
  }

  updateEmployee(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.updateEmployee;
    return this.http.post<any>(link, body);
  }
}
